import React from 'react';
import { VoiceModal, ModalFooter, Field, Select, TextInput, Checkbox, TEXT_MUTED } from '../components';
import { formatPhoneNumber } from '../components/format';

/**
 * Where inbound calls to a number should go.
 *
 * Only the fields relevant to the chosen route type are shown — the original
 * rendered all of them and let the user fill in ones the backend would
 * ignore.
 */
const ROUTE_TYPES = [
  { value: 'sip_credential', label: 'SIP credential', hint: 'Ring a registered softphone' },
  { value: 'sip_uri', label: 'SIP URI', hint: 'Forward to an external SIP address' },
  { value: 'forward', label: 'Forward to number', hint: 'Ring another phone number' },
];

const RoutingModal = ({ open, did, form, setForm, credentials = [], onCancel, onSave, saving }) => {
  if (!form) return null;

  const update = (patch) => setForm({ ...form, ...patch });
  const updateFailover = (patch) => setForm({ ...form, failover: { ...form.failover, ...patch } });

  const activeType = ROUTE_TYPES.find((t) => t.value === form.type);

  return (
    <VoiceModal
      open={open}
      onClose={onCancel}
      title="Call routing"
      subtitle={did ? formatPhoneNumber(did.phoneNumber) : undefined}
      footer={
        <ModalFooter onCancel={onCancel} onConfirm={onSave} confirmLabel="Save routing" loading={saving} />
      }
    >
      <div className="space-y-5">
        <Field label="Route incoming calls to" hint={activeType?.hint}>
          <Select value={form.type} onChange={(e) => update({ type: e.target.value })}>
            {ROUTE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>

        {form.type === 'sip_credential' && (
          <Field label="SIP credential" required>
            <Select
              value={form.sipCredentialId}
              onChange={(e) => update({ sipCredentialId: e.target.value })}
            >
              <option value="">
                {credentials.length ? 'Select a credential…' : 'No credentials on this trunk'}
              </option>
              {credentials.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.username}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {form.type === 'sip_uri' && (
          <Field label="SIP URI" required hint="e.g. sip:support@example.com">
            <TextInput
              value={form.sipUri}
              onChange={(e) => update({ sipUri: e.target.value })}
              placeholder="sip:user@host"
            />
          </Field>
        )}

        {form.type === 'forward' && (
          <Field label="Forward to" required hint="In E.164 format, e.g. +14155550100">
            <TextInput
              value={form.forwardTo}
              onChange={(e) => update({ forwardTo: e.target.value })}
              placeholder="+1…"
            />
          </Field>
        )}

        <div className="pt-4 border-t border-gray-200 dark:border-white/[0.08] space-y-4">
          <Checkbox
            label="Enable failover"
            description="Where the call goes if the primary route doesn't answer"
            checked={form.failover?.enabled}
            onChange={(e) => updateFailover({ enabled: e.target.checked })}
          />

          {form.failover?.enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-7">
              <Field label="Failover to">
                <Select
                  value={form.failover.type}
                  onChange={(e) => updateFailover({ type: e.target.value })}
                >
                  <option value="voicemail">Voicemail</option>
                  <option value="forward">Another number</option>
                  <option value="hangup">Hang up</option>
                </Select>
              </Field>

              {form.failover.type === 'forward' && (
                <Field label="Destination">
                  <TextInput
                    value={form.failover.destination}
                    onChange={(e) => updateFailover({ destination: e.target.value })}
                    placeholder="+1…"
                  />
                </Field>
              )}

              <Field label="Ring for" hint="Seconds before failing over">
                <TextInput
                  type="number"
                  min={5}
                  max={120}
                  value={form.failover.timeoutSeconds}
                  onChange={(e) =>
                    updateFailover({ timeoutSeconds: Number(e.target.value) || 30 })
                  }
                />
              </Field>
            </div>
          )}
        </div>

        <p className={`text-[12px] ${TEXT_MUTED}`}>
          Routing changes take effect on the next inbound call.
        </p>
      </div>
    </VoiceModal>
  );
};

export default RoutingModal;
